import React, { useEffect, useMemo } from "react";
import type { CallbackEvent } from "@shopify/polaris-types";
import { useNavigate, useParams } from "react-router-dom";
import { PageBreadcrumbs } from "../../../components/common/PageBreadcrumbs";
import { NavigationBlocker } from "../../../components/common/NavigationBlocker";
import { useShopifySaveBar } from "../../../hooks/useShopifySaveBar";
import { useShopCurrency } from "../../../hooks/useShopCurrency";
import {
  useOneTickEditorStore,
  selectOneTickIsDirty,
} from "../../../store/useOneTickEditorStore";
import { useOffersListStore } from "../../../store/useOffersListStore";
import { SalesBoosterTabBar } from "../SalesBoosterTabBar";
import { NameSection, TriggerSection } from "./OneTickEditorConfig";
import { DesignSection } from "./OneTickEditorDesign";
import type {
  OneTickDesignChange,
  OneTickDesignValues,
} from "./OneTickEditorDesign";
import { OneTickEditorPreview } from "./OneTickEditorPreview";

// ─── Page ─────────────────────────────────────────────────────────────────────

const OneTickEditor: React.FC = () => {
  const navigate = useNavigate();
  const { id: routeId } = useParams<{ id: string }>();

  // Editor state from the store
  const internalName = useOneTickEditorStore((s) => s.config.internalName);
  const isActive = useOneTickEditorStore((s) => s.config.isActive);
  const triggerType = useOneTickEditorStore((s) => s.config.triggerType);
  const triggerProducts = useOneTickEditorStore(
    (s) => s.config.triggerProducts
  );
  const upsellTitle = useOneTickEditorStore((s) => s.config.upsellTitle);
  const upsellPrice = useOneTickEditorStore((s) => s.config.upsellPrice);
  const checkboxTemplate = useOneTickEditorStore(
    (s) => s.config.checkboxTemplate
  );
  const description = useOneTickEditorStore((s) => s.config.description);
  const textColor = useOneTickEditorStore((s) => s.config.textColor);
  const descriptionColor = useOneTickEditorStore(
    (s) => s.config.descriptionColor
  );
  const linkedProductEnabled = useOneTickEditorStore(
    (s) => s.config.linkedProductEnabled
  );
  const selectedProduct = useOneTickEditorStore(
    (s) => s.config.selectedProduct
  );
  const imageUrl = useOneTickEditorStore((s) => s.config.imageUrl);
  const backgroundColor = useOneTickEditorStore(
    (s) => s.config.backgroundColor
  );
  const borderStyle = useOneTickEditorStore((s) => s.config.borderStyle);
  const borderWidth = useOneTickEditorStore((s) => s.config.borderWidth);
  const borderColor = useOneTickEditorStore((s) => s.config.borderColor);
  const borderRadius = useOneTickEditorStore((s) => s.config.borderRadius);

  // Status
  const offerId = useOneTickEditorStore((s) => s.offerId);
  const isLoading = useOneTickEditorStore((s) => s.isLoading);
  const isSaving = useOneTickEditorStore((s) => s.isSaving);
  const isDirty = useOneTickEditorStore(selectOneTickIsDirty);

  // Actions
  const updateField = useOneTickEditorStore((s) => s.updateField);
  const initNew = useOneTickEditorStore((s) => s.initNew);
  const loadFromBackend = useOneTickEditorStore((s) => s.loadFromBackend);
  const save = useOneTickEditorStore((s) => s.save);
  const discardChanges = useOneTickEditorStore((s) => s.discardChanges);
  const reset = useOneTickEditorStore((s) => s.reset);

  const refetchOffers = useOffersListStore((s) => s.refetch);

  const money = useShopCurrency();

  const designValues = useMemo<OneTickDesignValues>(
    () => ({
      upsellTitle,
      upsellPrice,
      checkboxTemplate,
      description,
      textColor,
      descriptionColor,
      linkedProductEnabled,
      selectedProduct,
      imageUrl,
      backgroundColor,
      borderStyle,
      borderWidth,
      borderColor,
      borderRadius,
    }),
    [
      upsellTitle,
      upsellPrice,
      checkboxTemplate,
      description,
      textColor,
      descriptionColor,
      linkedProductEnabled,
      selectedProduct,
      imageUrl,
      backgroundColor,
      borderStyle,
      borderWidth,
      borderColor,
      borderRadius,
    ]
  );

  useEffect(() => {
    if (routeId) {
      if (offerId !== routeId) {
        loadFromBackend(routeId).catch(() => {});
      }
      return;
    }
    if (offerId === null) {
      initNew();
    }
  }, [routeId, offerId, loadFromBackend, initNew]);

  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  useShopifySaveBar({
    isDirty,
    isSaving,
    onSave: async () => {
      const wasNew = offerId === null;
      const savedId = await save();
      refetchOffers().catch(() => {});
      if (wasNew) {
        navigate(`/sales-booster/upsells-1-tick/${savedId}`, {
          replace: true,
        });
      }
    },
    onDiscard: discardChanges,
  });

  return (
    <div>
      <NavigationBlocker isDirty={isDirty} />

      <s-page heading={internalName || "Nuevo upsell"}>
        <PageBreadcrumbs
          items={[
            { label: "Impulsor de ventas", to: "/sales-booster" },
            { label: "Upsells 1-Tick", to: "/sales-booster/upsells-1-tick" },
          ]}
        />
        <s-stack direction="block" gap="large-200">
          {/* ── Tab bar ── */}
          <SalesBoosterTabBar />

          {/* ── Header (active toggle + status) ── */}
          <div className="flex items-center justify-end py-1">
            <div className="flex items-center gap-3">
              <s-switch
                label="Activar upsell"
                labelAccessibilityVisibility="exclusive"
                checked={isActive}
                onChange={(e: CallbackEvent<"s-switch">) =>
                  updateField("isActive", e.currentTarget.checked)
                }
              />
              <span
                className={`px-2.5 py-1 rounded-full text-xs font-semibold tracking-wide border ${
                  isActive
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-gray-100 text-gray-500 border-gray-200"
                }`}
              >
                {isActive ? "Activo" : "Inactivo"}
              </span>
            </div>
          </div>

          {isLoading && (
            <s-banner tone="info">Cargando configuración del upsell…</s-banner>
          )}

          {/* ── Two-column layout: settings on the left, live preview on
              the right. ── */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 items-start">
            {/* Left: settings */}
            <s-stack direction="block" gap="base">
              <NameSection
                value={internalName}
                onChange={(v) => updateField("internalName", v)}
              />
              <TriggerSection
                value={triggerType}
                onChange={(v) => updateField("triggerType", v)}
                products={triggerProducts}
                onProductsChange={(v) => updateField("triggerProducts", v)}
              />
              <DesignSection
                values={designValues}
                onDesignChange={updateField as OneTickDesignChange}
                money={money}
              />
            </s-stack>

            {/* Right: preview */}
            <OneTickEditorPreview
              textTemplate={checkboxTemplate}
              productTitle={upsellTitle}
              priceLabel={money.formatPrice(upsellPrice)}
              description={description}
              imageUrl={imageUrl}
              textColor={textColor}
              descriptionColor={descriptionColor}
              styles={{
                backgroundColor,
                borderStyle,
                borderWidth,
                borderColor,
                borderRadius,
              }}
            />
          </div>
        </s-stack>
      </s-page>
    </div>
  );
};

export default OneTickEditor;
